// Campaign domain types for the frontend.
//
// The canonical contract (ICampaign, CampaignStatus) lives in `@atlas/types`. This
// module re-exports the enum and defines the FRONTEND VIEW shape, pinned to the
// real hydrated Campaign HTTP payload — `{ ...campaign.toDTO(), classification,
// veevaCode }` (`campaignPayload()` in campaign-controller.helpers.ts) — so the
// HTTP-backed adapters need no reshaping. Nothing here carries a scripted
// presentation field (available actions, command outcomes, loading/error flags,
// capabilities) — those are presentation-scenario concerns.

import type { AxisSelection, DTOOf, ICampaign, IFileWithTags, ObjectTag } from '@atlas/types'
import { CampaignStatus } from '@atlas/types'

export { CampaignStatus }

/**
 * The Campaign list's fixed page size — pinned, never re-derived, never discovered
 * from a backend response. Intentionally matches the backend's
 * `QUERY_CONFIG.DEFAULT_LIMIT` (`campaign-controller.helpers.ts`), also `20`. Used
 * as `limit` for the initial request, every load-more request, and every
 * filter-reset request; the `hasAnyCampaignsAtAll` existence probe is the only
 * call site that uses a different limit (`1`).
 */
export const CAMPAIGN_LIST_PAGE_SIZE = 20

/**
 * The largest `limit` a Campaign list request can actually return. Intentionally
 * matches the ceiling the shared `buildListQuery` clamps to (`database-utils`),
 * so a caller wanting "as many as possible" asks for a number the server will
 * honour instead of one it silently reduces.
 */
export const CAMPAIGN_LIST_MAX_LIMIT = 100

/**
 * Backend request-level page size for the associated-Files and File-discovery
 * HTTP adapters. Intentionally matches `fileDiscoverRequestSchema`'s verified
 * `limit.max(100)` (`file.schema.ts`) — not re-derived, never discovered from a
 * response.
 */
export const CAMPAIGN_FILES_API_PAGE_SIZE = 100

/**
 * The accumulation ceiling for associated Files and discovery candidates — at
 * most two backend pages of `CAMPAIGN_FILES_API_PAGE_SIZE`. A stated MVP
 * product boundary, not a mirror of any backend constraint.
 */
export const CAMPAIGN_FILES_MVP_LIMIT = 200

/**
 * Maximum number of File ids a single association confirm action may submit.
 * Intentionally matches `campaignFileAssociationSchema`'s verified
 * `fileIds.max(100)` (`campaign.schema.ts`).
 */
export const CAMPAIGN_FILE_ASSOCIATION_BATCH_LIMIT = 100

/**
 * Maximum number of Message ids a single association confirm action may submit.
 * Intentionally matches `campaignMessageAssociationSchema`'s verified
 * `messageIds.max(100)` (`campaign.schema.ts`). Equal to the File cap today, but
 * named separately: the two are set by two different server schemas and nothing
 * keeps them in step.
 */
export const CAMPAIGN_MESSAGE_ASSOCIATION_BATCH_LIMIT = 100

/**
 * Every lifecycle state except `ARCHIVED` — what a consultation surface means by "the
 * campaigns that apply here". An archived campaign is history, so it is excluded by the
 * request rather than fetched and hidden.
 *
 * Derived from the enum rather than spelled out, so a lifecycle state added later is
 * included by default and only a deliberate exclusion has to be written down.
 */
export const NON_ARCHIVED_CAMPAIGN_STATUSES: readonly CampaignStatus[] = Object.values(CampaignStatus).filter(
	(status) => status !== CampaignStatus.ARCHIVED,
)

/** Human-readable label for each Campaign lifecycle state — mirrors `lib/roles.ts`'s `ROLE_LABELS` pattern. */
export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
	[CampaignStatus.DRAFT]: 'Draft',
	[CampaignStatus.IN_PROGRESS]: 'In progress',
	[CampaignStatus.IN_REVIEW]: 'In review',
	[CampaignStatus.APPROVED]: 'Approved',
	[CampaignStatus.LIVE]: 'Live',
	[CampaignStatus.ARCHIVED]: 'Archived',
}

/** A Campaign's resolved Classification: code-only `selection` plus full `tags` for display. */
export interface CampaignClassification {
	selection: AxisSelection
	tags: ObjectTag[]
}

/**
 * A Campaign as rendered by list/detail — mirrors the campaign-service
 * `campaignPayload()` DTO exactly: the full `ICampaign` shape (Dates as ISO
 * strings) plus `classification` and `veevaCode`. Carries no available actions,
 * transition outcome, delete outcome, loading/error flag, or capability value —
 * the real payload carries none of these, so neither does this type.
 */
export type Campaign = DTOOf<ICampaign> & {
	classification: CampaignClassification
	veevaCode: string | null
}

/**
 * A File associated with a Campaign — mirrors `listCampaignFiles`'s
 * `{ ...file.toDTO(), tags }` DTO exactly (the shared File DTO plus resolved
 * Classification tags). No parallel "Asset" type exists; the internal object is
 * File throughout, and "asset" is user-facing wording only.
 */
export type CampaignFile = DTOOf<IFileWithTags>

/**
 * Body for creating a Campaign. Re-exported from `@atlas/models`'
 * `campaignCreateSchema` inference rather than hand-mirrored, so the frontend
 * form model can never drift from the backend's optional-vs-nullable split.
 */
export type {
	CampaignCreateInput as CreateCampaignInput,
	CampaignUpdateInput as UpdateCampaignInput,
} from '@atlas/models'

/**
 * The resolved, immutable Campaign action-capability data shape handed to
 * presentation. Carries no read capability — Campaign read access is owned
 * exclusively by route authorization (minimum role USER + VIEWER denial), so no
 * read-capability field exists here or anywhere else in this feature.
 * `canAssociateFiles` and `canUploadFiles` are separate fields set independently;
 * no consumer may derive one from the other.
 */
export interface CampaignCapabilities {
	readonly canCreate: boolean
	readonly canEdit: boolean
	readonly canTransition: boolean
	readonly canAssociateFiles: boolean
	readonly canUploadFiles: boolean
	readonly canAssociateMessages: boolean
	readonly canTranslateMessages: boolean
	/** Editing an associated Message's copy in place, from the Campaign's resolution matrix. */
	readonly canEditMessages: boolean
	readonly canDelete: boolean
}

/** Whether a download operation is currently available. */
export type CampaignDownloadAvailability = 'available' | 'unavailable'

/**
 * Download-capability state for a Campaign's Files — `individual`, `selected`,
 * `all`, and `package` as fully independent availability values, so no consumer
 * can infer one operation's availability from another's.
 */
export interface CampaignDownloadCapabilityState {
	readonly individual: CampaignDownloadAvailability
	readonly selected: CampaignDownloadAvailability
	readonly all: CampaignDownloadAvailability
	readonly package: CampaignDownloadAvailability
}
